import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import {
  LoadAccountMemberList, LoadFormList, LoadProjectList, LoadResourcePlaceList, LoadWorkFinishOptionList
} from '@/javascript/domain/usecases'
import { AutocompleteFetch, ResourcePlacesInput } from '@/javascript/presentation/components'
import { ViewModeInput, WorkStatusInput, WorkFinishOptionsInput } from './components'
import fetchDataToInput, { toInputData } from './helpers/data-to-input'
import { FetchLocationsParams } from '../../work-user-maps'
import { FilterButton } from '@/javascript/presentation/components/buttons'

type Props = {
  fetchLocations: (params?: FetchLocationsParams) => Promise<void>
  fetchAccountMembers: LoadAccountMemberList
  fetchForms: LoadFormList
  fetchProjects: LoadProjectList
  fetchResourcePlaces: LoadResourcePlaceList
  fetchWorkFinishOptions: LoadWorkFinishOptionList
  isReady: boolean,
  isActive: boolean,
  date?: dayjs.Dayjs | null,
  setDate?: React.Dispatch<React.SetStateAction<dayjs.Dayjs | null>>,
}

type ResourcePlace = {
  value: string
  label: string
  hierarchyLatitude?: number
  hierarchyLongitude?: number
}

const viewOptions = [{ label: "Atividades", value: "works" }, { label: "Equipe", value: "users" }]
const workStatusOptions =[
  { label: "A realizar", value: "not_started" }, { label: "Em andamento", value: "started" },
  { label: "Finalizado", value: "finished" }, { label: "Revisado", value: "reviewed" }
]

const Filters: React.FC<Props> = (props) => {
  const [viewMode, setViewMode] = useState([viewOptions[0], viewOptions[1]])
  const [forms, setForms] = useState([])
  const [resourcePlaces, setResourcePlaces] = useState<ResourcePlace[]>([])
  const [projects, setProjects] = useState([])
  const [inspectors, setInspectors] = useState([])
  const [providers, setProviders] = useState([])
  const [workStatus, setWorkStatus] = useState('')
  const [finishOptions, setFinishOptions] = useState([])
  const lastFetchedKey = React.useRef<string | null>(null)
  const handleRequestFilters = (date: dayjs.Dayjs | null, viewMode: { label: string, value: string }[]) => {
    if (date) {
      props.fetchLocations({
        date: date.toDate(),
        viewMode: viewMode.map(option => option.value),
        resourcePlaces: resourcePlaces.map(option => option.value),
        forms: forms.map(option => option.value),
        projects: projects.map(option => option.value),
        providers: providers.map(option => option.value),
        inspectors: inspectors.map(option => option.value),
        finishOptions: finishOptions.map(option => option.value),
        status: workStatus
      })
    }
  }

  useEffect(() => {
    if (!props.isActive) return
    const key = JSON.stringify({
      date: props.date?.format('YYYY-MM-DD'),
      viewMode: viewMode.map(v => v.value),
      resourcePlaces: resourcePlaces.map(o => o.value),
      forms: forms.map(o => o.value),
      projects: projects.map(o => o.value),
      providers: providers.map(o => o.value),
      inspectors: inspectors.map(o => o.value),
      workStatus,
      finishOptions: finishOptions.map(o => o.value)
    })
    if (key === lastFetchedKey.current) return
    lastFetchedKey.current = key
    handleRequestFilters(props.date, viewMode)
  }, [props.isActive, props.date, viewMode, resourcePlaces, forms, projects, inspectors, providers, workStatus, finishOptions])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }} data-testid="map-filter">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <ViewModeInput viewMode={viewMode} setViewMode={setViewMode} viewOptions={viewOptions} />
        <ResourcePlacesInput
          resourcePlaces={resourcePlaces} setResourcePlaces={setResourcePlaces}
          fetchResourcePlaces={(searchParam) => fetchDataToInput(props.fetchResourcePlaces, searchParam)}
          optionsProps={{
            tooltipTitle: 'Não possui endereço cadastrado',
            disable: (option: ResourcePlace) => !option.hierarchyLatitude || !option.hierarchyLongitude
          }}
        />
        <AutocompleteFetch
          label="Formulário/Checklist" value={forms} setValue={setForms}
          fetchOptions={(searchParam) => fetchDataToInput(props.fetchForms, searchParam)}
        />
        <AutocompleteFetch
          label="Projetos" value={projects} setValue={setProjects}
          fetchOptions={(searchParam) => fetchDataToInput(props.fetchProjects, searchParam)}
        />
        <WorkStatusInput workStatus={workStatus} setWorkStatus={setWorkStatus} workStatusOptions={workStatusOptions} />
        <WorkFinishOptionsInput
          workFinishOptions={finishOptions} setWorkFinishOptions={setFinishOptions}
          fetchWorkFinishOptions={(searchParam) => props.fetchWorkFinishOptions.load({ searchText: searchParam, onlyActive: true }).then(toInputData).catch((): any[] => [])}
          workStatus={workStatus}
        />
        <AutocompleteFetch
          label="Responsáveis - Acompanhamento" value={inspectors} setValue={setInspectors}
          fetchOptions={(searchParam) => props.fetchAccountMembers.load({ searchText: searchParam, onlyActive: true }).then(toInputData).catch((): any[] => [])}
        />
        <AutocompleteFetch
          label="Responsáveis - Execução" value={providers} setValue={setProviders}
          fetchOptions={(searchParam) => props.fetchAccountMembers.load({ searchText: searchParam, onlyActive: true }).then(toInputData).catch((): any[] => [])}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mr: 2 }}>
        <FilterButton
          variant="clear"
          disabled={
            !props.isReady || (props.date && dayjs(props.date).isSame(dayjs(), 'date') && viewMode.length === 2 && finishOptions.length === 0 &&
            resourcePlaces.length === 0 && forms.length === 0 && projects.length === 0
            && inspectors.length === 0 && providers.length === 0 && workStatus === '')
          }
          onClick={() => {
            if (props.setDate) props.setDate(dayjs())
            setViewMode([viewOptions[0], viewOptions[1]])
            setResourcePlaces([])
            setForms([])
            setProjects([])
            setInspectors([])
            setProviders([])
            setFinishOptions([])
            setWorkStatus('')
          }}
        />
      </Box>
    </Box>
  )
}

export default Filters
